import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text, useColorMode } from '@chaine/keychaine'
import { uniqueId } from 'lodash'
import React, { useRef, useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import * as Yup from 'yup'

import { Toast } from '../../../_shared/components/toast'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_FOUR_KITES_CARRIER_APP, FourKitesCarrierEnableAndUpdateAppInput } from '../data/mutations'
import {
  baseValidationSchema,
  getGroupStructure,
  getPayloadStructure,
  validationSchema
} from '../utils/four-kites-modal-utils'
import { FourKitesCarrierUpdateModal } from './four-kites-carrier-update-modal'

/**
 * Saves FourKitesCarrierModal app's encoded api key with customer Ids.
 *
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */

type TFieldValues = { [key: string]: string; apiKey: string }

const FourKitesCarrierModal = React.memo((props: AppModalProps) => {
  const [schema, setSchema] = useState(baseValidationSchema.concat(validationSchema(1)))
  const { appType, isOpen, refetchApps, onClose, isModalEditable, setModalEditable, app } = props
  const [formError, setFormError] = useState('')
  const { showToast } = Toast()
  const [customers, setCustomers] = useState(() => getGroupStructure(1))
  const deletedGroups = useRef<string[]>([])
  const { colorMode } = useColorMode()

  const updateSchema = (uniqueID: string | number) => {
    setSchema((prevSchema) => prevSchema.concat(validationSchema(uniqueID)))
  }

  const removeFieldsFromSchema = (id: string) => {
    setSchema((prevSchema) => {
      deletedGroups.current.push(id)
      const currentFields = prevSchema.fields
      const filteredFields = Object.keys(currentFields)
        .filter((key) => ![`customerID-${id}`, `customerIDInChaine-${id}`, `customerNameInChaine-${id}`].includes(key))
        .reduce((acc, key) => {
          acc[key] = currentFields[key]
          return acc
        }, {} as Record<string, Yup.AnySchema>)
      return Yup.object(filteredFields) as typeof prevSchema
    })
  }

  const handleAddMore = () => {
    const uniqueID = uniqueId()
    const newlyAddedGroup = getGroupStructure(uniqueID)
    updateSchema(uniqueID)
    setCustomers((old) => {
      return {
        ...old,
        ...newlyAddedGroup
      }
    })
  }

  const closeModal = () => {
    setFormError('')
    onClose()
    if (setModalEditable) {
      setModalEditable(false)
    }
  }

  const handleGroupDelete = (groupID: string) => {
    if (Object.keys(customers).length > 1) {
      removeFieldsFromSchema(groupID)
      setCustomers((old) => {
        const newGroups = { ...old }
        delete newGroups[groupID]
        return newGroups
      })
    }
  }

  const [enableFourKitesCarrierApp, { loading }] = useMutation<
    { enableFourKitesCarrierApp: FourKitesCarrierEnableAndUpdateAppInput },
    FourKitesCarrierEnableAndUpdateAppInput
  >(ENABLE_FOUR_KITES_CARRIER_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `Four Kites Carrier app is enabled successfully!`
      })
      refetchApps()
      onClose()
      setFormError('')
    },
    onError: (err) => {
      setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
      showToast({
        status: 'error',
        title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
      })
    }
  })

  const handleSaveDetails: SubmitHandler<TFieldValues> = (event) => {
    deletedGroups.current.forEach((id) => {
      delete event[`customerID-${id}`]
      delete event[`customerIDInChaine-${id}`]
      delete event[`customerNameInChaine-${id}`]
    })
    enableFourKitesCarrierApp({
      variables: {
        appType: appType,
        ...getPayloadStructure(event)
      }
    })
  }

  const footer = (
    <Box>
      <Text color="red.600">{formError}</Text>
      <Text>
        {`See `}
        <Link
          color="blue.600"
          href="https://handbook.chaineapp.com/product-guide/apps/four-kites"
          target="_blank"
          rel="noopener noreferrer"
        >
          detailed steps here.
        </Link>
      </Text>
    </Box>
  )

  return isModalEditable ? (
    <FourKitesCarrierUpdateModal
      app={app}
      appType={appType}
      isOpen={isOpen}
      onClose={closeModal}
      refetchApps={refetchApps}
      isModalEditable
    />
  ) : (
    <FormModal
      isOpen={isOpen}
      onClose={closeModal}
      size="3xl"
      title={`Enable FourKites for Chaine`}
      subtitle={`Enabling this integration requires the workspace and customer ids.`}
      formSubmitHandler={handleSaveDetails}
      buttonText={'Enable'}
      validationSchema={schema}
      isLoading={loading}
      footer={formError ? footer : <></>}
      addMoreHandler={handleAddMore}
      onGroupDelete={handleGroupDelete}
      modalBodyProps={{ maxHeight: 600, overflow: 'auto' }}
      modalItems={[
        {
          isRequired: true,
          labelText: 'API Key',
          name: 'apiKey',
          placeHolder: 'Enter an API Key',
          type: 'input'
        },
        {
          direction: 'column',
          inputGroups: customers,
          labelText: 'Customer Ids',
          name: 'inputGroups',
          otherProps: {
            backgroundColor: colorMode === 'dark' ? 'black' : 'gray.50',
            borderRadius: 12,
            gap: 2,
            padding: 4
          },
          type: 'inputGroups'
        }
      ]}
    />
  )
})

FourKitesCarrierModal.displayName = 'FourKitesCarrierModal'

export { FourKitesCarrierModal }
